import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { User, Shield, Award, Users, Briefcase, BookOpen, Settings, Calculator } from 'lucide-react';
import { governanceData } from '../data/mock';

const iconMap = {
  Settings,
  BookOpen,
  Calculator
};

const MemberCard = ({ member, type }) => {
  // Function to generate avatar with initials
  const getInitials = (name) => {
    if (name === "Board of Directors") return "BD";
    if (name === "Independent Director") return "ID";
    if (name === "Governing Council Member") return "GC";
    if (name === "Advisory Board Member") return "AB";
    if (name === "Director General") return "DG";
    
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
      <CardContent className="p-6">
        {/* Photo/Avatar Section */}
        <div className="flex flex-col items-center mb-4">
          {member.photo ? (
            <img
              src={member.photo}
              alt={member.name}
              className="w-32 h-32 rounded-full object-cover border-4 border-[#E87722] shadow-lg"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/80 flex items-center justify-center border-4 border-[#E87722] shadow-lg">
              <span className="text-white text-3xl font-bold">
                {getInitials(member.name)}
              </span>
            </div>
          )}
        </div>

        {/* Member Info */}
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">
            {member.name}
          </h3>
          <div className="inline-flex items-center justify-center px-3 py-1 bg-[#E87722]/10 rounded-full mb-3">
            <span className="text-sm font-semibold text-[#E87722]">
              {member.role}
            </span>
          </div>
        </div>

        {/* Profile */}
        <p className="text-gray-600 text-sm leading-relaxed text-center">
          {member.profile}
        </p>
      </CardContent>
    </Card>
  );
};

const DepartmentCard = ({ department }) => {
  const Icon = iconMap[department.icon];
  
  return (
    <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-l-4 border-[#E87722]">
      <CardHeader>
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-3 bg-[#E87722]/10 rounded-lg">
            <Icon className="text-[#E87722]" size={28} />
          </div>
          <div>
            <CardTitle className="text-xl text-[#1E3A5F]">
              {department.name}
            </CardTitle>
            <CardDescription className="text-sm text-[#E87722] font-semibold">
              {department.role}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600">{department.description}</p>
      </CardContent>
    </Card>
  );
};

const Governance = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-20 pt-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#E87722] rounded-full">
                <Shield size={48} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Governance Structure
            </h1>
            <p className="text-lg text-gray-200 leading-relaxed">
              {governanceData.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Board of Directors Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#1E3A5F]/10 rounded-full">
                <Briefcase className="text-[#1E3A5F]" size={36} />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              Board of Directors
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The Board serves as the legal and fiduciary authority of CSED, statutorily responsible
              for strategic approval, financial oversight, and institutional governance under Section 8
              of the Companies Act.
            </p>
          </div>

          {/* Founding Directors - Stella & Xavier (2 columns) */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-8 text-center">Founding Directors</h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {governanceData.board.filter(member => member.id <= 2).map((member) => (
                <MemberCard key={member.id} member={member} type="board" />
              ))}
            </div>
          </div>

          {/* Independent Directors (3 columns) */}
          <div>
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-8 text-center">Independent Directors</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {governanceData.board.filter(member => member.id > 2).map((member) => (
                <MemberCard key={member.id} member={member} type="board" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Governance Council Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#1E3A5F]/10 rounded-full">
                <Shield className="text-[#1E3A5F]" size={36} />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              Governing Council
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The Governing Council provides strategic oversight and serves as the institutional conscience,
              guiding CSED's long-horizon direction and ensuring credibility through intellectual leadership.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {governanceData.governanceCouncil.map((member) => (
              <MemberCard key={member.id} member={member} type="council" />
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Board Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#E87722]/10 rounded-full">
                <Award className="text-[#E87722]" size={36} />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              Advisory Board
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A flexible pool of domain experts providing technical validation, thematic depth,
              and specialized guidance across law, climate, development, industry, and policy domains.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {governanceData.advisoryBoard.map((member) => (
              <MemberCard key={member.id} member={member} type="advisory" />
            ))}
          </div>
        </div>
      </section>

      {/* Secretariat Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[#E87722]/10 rounded-full">
                <Users className="text-[#E87722]" size={36} />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-4">
              Secretariat
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              The Executive Secretariat converts strategy into execution and governance intent
              into operational systems, led by the Director General.
            </p>
          </div>

          {/* Director General */}
          <div className="max-w-2xl mx-auto mb-12">
            <MemberCard member={governanceData.secretariat.directorGeneral} type="dg" />
          </div>

          {/* Departments */}
          <div className="max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold text-[#1E3A5F] text-center mb-8">
              Operational Divisions
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {governanceData.secretariat.departments.map((dept, index) => (
                <DepartmentCard key={index} department={dept} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integrity Statement */}
      <section className="py-16 bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <div className="flex items-center space-x-3 mb-2">
                  <Shield className="text-[#E87722]" size={28} />
                  <CardTitle className="text-2xl text-white">
                    Institutional Integrity & Ethics
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-gray-200">
                  <p className="leading-relaxed">
                    CSED operates under statutory governance requirements with board
                    oversight, transparent reporting frameworks, and robust conflict-of-interest
                    safeguards.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#E87722] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white mb-1">
                          Transparent Governance
                        </h4>
                        <p className="text-sm text-gray-300">
                          Regular board meetings, documented decisions, and public
                          accountability
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#E87722] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white mb-1">
                          Conflict of Interest Policy
                        </h4>
                        <p className="text-sm text-gray-300">
                          Strict protocols for disclosure and management of potential
                          conflicts
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#E87722] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white mb-1">
                          Financial Accountability
                        </h4>
                        <p className="text-sm text-gray-300">
                          Annual audits, transparent fund utilization, and compliance with
                          Section 8 regulations
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#E87722] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-white mb-1">
                          Independent Oversight
                        </h4>
                        <p className="text-sm text-gray-300">
                          External evaluation, third-party audits, and stakeholder
                          accountability
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Governance;
